import { Button } from "@shared-ui/button";
import { Input } from "@shared-ui/input";
import { Label } from "@shared-ui/label";
import { Textarea } from "@shared-ui/textarea";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Dialog } from "@/components/ui/dialog";
import {
	GstRatePreviewPanel,
	GstRepriceSummary,
} from "@/features/catalog/components/gst-rate-preview";
import {
	formatRate,
	formatRateDay,
	isWebUrl,
	PREFIX_PATTERN,
	parseRatePercent,
	todayUtc,
} from "@/features/catalog/lib/gst-rates";
import {
	addGstRate,
	correctGstRate,
	fieldErrorsOf,
	type GstRate,
	type GstRatePreview,
	type GstRepriceResult,
	getGstPrefixChain,
	isStalePreview,
	previewGstRateChange,
	repriceGstPrefix,
} from "@/features/catalog/services/gst-rates-api.service";
import { apiErrorMessage } from "@/lib/api-error";

/**
 * Add a GST rate, or correct one that was entered wrong (#514).
 *
 * THREE STEPS IN ONE DIALOG: the form, a review of what the change does to
 * customer prices, and the result of saving it. The review is required --
 * Save exists only on it -- and it is taken from the values on the form at the
 * moment "Review effect" is pressed; going Back discards it.
 */

const TITLE_ID = "gst-rate-dialog-title";

export type GstRateDialogMode =
	| { kind: "add"; prefix?: string }
	| { kind: "correct"; rate: GstRate };

type Step =
	| { name: "form" }
	| { name: "review"; preview: GstRatePreview }
	| { name: "done"; headline: string; prefix: string }
	| { name: "unknown" };

interface Props {
	mode: GstRateDialogMode;
	onClose: () => void;
	/** Called after anything was saved, so the screen behind can refresh. */
	onChanged: () => void;
}

export function GstRateDialog({ mode, onClose, onChanged }: Props) {
	const correcting = mode.kind === "correct" ? mode.rate : null;

	const [prefix, setPrefix] = useState(
		correcting?.hsnPrefix ?? (mode.kind === "add" ? (mode.prefix ?? "") : ""),
	);
	const [rate, setRate] = useState("");
	const [effectiveFrom, setEffectiveFrom] = useState(
		correcting ? correcting.effectiveFrom.slice(0, 10) : "",
	);
	const [source, setSource] = useState(correcting?.source ?? "");
	const [serial, setSerial] = useState("");
	const [signedOffBy, setSignedOffBy] = useState("");
	const [signedOffOn, setSignedOffOn] = useState(todayUtc());
	const [evidenceUrl, setEvidenceUrl] = useState("");
	const [note, setNote] = useState("");
	const [reason, setReason] = useState("");
	const [step, setStep] = useState<Step>({ name: "form" });
	const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
	const [notice, setNotice] = useState<string | null>(null);
	const [result, setResult] = useState<GstRepriceResult | null>(null);

	const prefixIsValid = PREFIX_PATTERN.test(prefix.trim());
	const chain = useQuery({
		queryKey: ["admin", "catalog", "gst-rates", "prefix", prefix.trim()],
		queryFn: () => getGstPrefixChain(prefix.trim()),
		enabled: mode.kind === "add" && prefixIsValid,
	});
	const latest =
		chain.data?.rows.find((row) => row.id === chain.data?.latestRateId) ?? null;

	const ratePercent = parseRatePercent(rate);
	const evidenceOk = evidenceUrl.trim() === "" || isWebUrl(evidenceUrl.trim());
	const formComplete =
		prefixIsValid &&
		ratePercent !== null &&
		/^\d{4}-\d{2}-\d{2}$/.test(effectiveFrom) &&
		source.trim() !== "" &&
		serial.trim() !== "" &&
		signedOffBy.trim() !== "" &&
		/^\d{4}-\d{2}-\d{2}$/.test(signedOffOn) &&
		evidenceOk &&
		(mode.kind === "add" || reason.trim() !== "");

	const preview = useMutation({
		mutationFn: () =>
			previewGstRateChange(
				correcting
					? {
							kind: "correct",
							id: correcting.id,
							ratePercent: ratePercent as number,
						}
					: {
							kind: "add",
							hsnPrefix: prefix.trim(),
							ratePercent: ratePercent as number,
							effectiveFrom,
						},
			),
		onSuccess: (data) => {
			setFieldErrors({});
			setStep({ name: "review", preview: data });
		},
		onError: (error) => {
			// Back to the form, which shows why: a stale review left on screen
			// after a failed re-preview could otherwise still be saved.
			setFieldErrors(fieldErrorsOf(error));
			setStep({ name: "form" });
		},
	});

	const save = useMutation({
		mutationFn: (latestRateId: string | null) => {
			const signOff = {
				signedOffBy: signedOffBy.trim(),
				signedOffOn,
				notificationSerial: serial.trim(),
				evidenceUrl: evidenceUrl.trim() || null,
			};
			return correcting
				? correctGstRate(correcting.id, {
						...signOff,
						ratePercent: ratePercent as number,
						source: source.trim(),
						note: note.trim() || null,
						reason: reason.trim(),
						expectedLatestRateId: latestRateId,
					})
				: addGstRate({
						...signOff,
						hsnPrefix: prefix.trim(),
						ratePercent: ratePercent as number,
						effectiveFrom,
						source: source.trim(),
						note: note.trim() || null,
						expectedLatestRateId: latestRateId,
					});
		},
		onSuccess: (data) => {
			onChanged();
			setResult(data.reprojection);
			setStep({
				name: "done",
				prefix: data.rate.hsnPrefix,
				headline: correcting
					? `Corrected ${data.rate.hsnPrefix} to ${formatRate(data.rate.rateBp)} from ${formatRateDay(data.rate.effectiveFrom)}.`
					: `Saved ${data.rate.hsnPrefix} at ${formatRate(data.rate.rateBp)} from ${formatRateDay(data.rate.effectiveFrom)}.`,
			});
		},
		onError: (error) => {
			if (isStalePreview(error)) {
				// The prefix changed under the preview: say so, take a fresh one,
				// and keep Save off until it has rendered.
				setNotice(apiErrorMessage(error, "This prefix's rates changed."));
				preview.mutate();
				return;
			}
			const fields = fieldErrorsOf(error);
			if (Object.keys(fields).length > 0) {
				setFieldErrors(fields);
				setStep({ name: "form" });
				return;
			}
			if (!(error as { response?: unknown }).response) {
				// No answer at all: the save may have committed before the
				// connection dropped.
				onChanged();
				setStep({ name: "unknown" });
			}
		},
	});

	const reprice = useMutation({
		mutationFn: (hsnPrefix: string) => repriceGstPrefix(hsnPrefix),
		onSuccess: (data) => {
			setResult(data);
			onChanged();
		},
	});

	const busy = save.isPending || preview.isPending || reprice.isPending;

	const fieldError = (name: string) =>
		fieldErrors[name] ? (
			<p className="text-xs text-red-700" role="alert">
				{fieldErrors[name]}
			</p>
		) : null;

	const title = correcting
		? `Correct ${correcting.hsnPrefix} from ${formatRateDay(correcting.effectiveFrom)}`
		: "Add a GST rate";

	return (
		<Dialog
			open
			titleId={TITLE_ID}
			onClose={() => {
				if (!busy) onClose();
			}}
			className="grid max-h-[90vh] w-full max-w-2xl gap-4 overflow-y-auto rounded-3xl border bg-white p-5 shadow-xl"
		>
			<div>
				<h2 id={TITLE_ID} className="text-lg font-semibold">
					{step.name === "review"
						? `Review: ${prefix.trim()} at ${formatRate(Math.round((ratePercent ?? 0) * 100))} from ${formatRateDay(`${effectiveFrom}T00:00:00.000Z`)}`
						: title}
				</h2>
				{step.name === "form" ? (
					<p className="text-sm text-muted-foreground">
						{correcting
							? `The ${formatRate(correcting.rateBp)} rate is withdrawn: kept in the history, never used to price. The corrected rate replaces it from the same date.`
							: "A new rate is a new row: it closes the prefix's current rate on the day it starts. Rates in force are never edited."}
					</p>
				) : null}
			</div>

			<div aria-live="polite" className="grid gap-3">
				{step.name === "form" ? (
					// Read-only while the review is worked out, so it describes what
					// is on the form.
					<fieldset disabled={preview.isPending} className="grid gap-3">
						<div className="grid gap-1.5">
							<Label htmlFor="gst-prefix">HSN or SAC prefix</Label>
							<Input
								id="gst-prefix"
								inputMode="numeric"
								value={prefix}
								disabled={mode.kind === "correct"}
								onChange={(event) => setPrefix(event.target.value)}
								placeholder="2523"
							/>
							{mode.kind === "add" && prefixIsValid && chain.isSuccess ? (
								<p className="text-xs text-muted-foreground">
									{latest
										? `Latest: ${formatRate(latest.rateBp)} from ${formatRateDay(latest.effectiveFrom)}, ${latest.effectiveTo ? `ending ${formatRateDay(latest.effectiveTo)}` : "open"}. A new rate starts after that, and not before today.`
										: "No rate is on record for this prefix yet, so the new rate may start on any date."}
								</p>
							) : (
								<p className="text-xs text-muted-foreground">
									2 to 8 digits. A product takes the rate of the longest prefix
									of its code.
								</p>
							)}
							{fieldError("hsnPrefix")}
						</div>

						<div className="grid gap-3 sm:grid-cols-2">
							<div className="grid gap-1.5">
								<Label htmlFor="gst-rate">
									{correcting ? "Correct rate (%)" : "Rate (%)"}
								</Label>
								<Input
									id="gst-rate"
									inputMode="decimal"
									value={rate}
									onChange={(event) => setRate(event.target.value)}
									placeholder="18"
								/>
								{rate.trim() !== "" && ratePercent === null ? (
									<p className="text-xs text-red-700">
										A rate from 0 to 40, with at most two decimals.
									</p>
								) : null}
								{fieldError("ratePercent")}
							</div>
							<div className="grid gap-1.5">
								<Label htmlFor="gst-from">Effective from</Label>
								<Input
									id="gst-from"
									type="date"
									value={effectiveFrom}
									disabled={mode.kind === "correct"}
									onChange={(event) => setEffectiveFrom(event.target.value)}
								/>
								<p className="text-xs text-muted-foreground">
									Takes effect at the start of this date (UTC).
								</p>
								{fieldError("effectiveFrom")}
							</div>
						</div>

						<div className="grid gap-1.5">
							<Label htmlFor="gst-source">Notification</Label>
							<Input
								id="gst-source"
								value={source}
								onChange={(event) => setSource(event.target.value)}
								placeholder="Notification No. 09/2025-Central Tax (Rate), dated 17-09-2025"
							/>
							{fieldError("source")}
						</div>
						<div className="grid gap-3 sm:grid-cols-2">
							<div className="grid gap-1.5">
								<Label htmlFor="gst-serial">S. No.</Label>
								<Input
									id="gst-serial"
									value={serial}
									onChange={(event) => setSerial(event.target.value)}
									placeholder="Schedule II S. No. 75"
								/>
								{fieldError("notificationSerial")}
							</div>
							<div className="grid gap-1.5">
								<Label htmlFor="gst-evidence">Evidence link (optional)</Label>
								<Input
									id="gst-evidence"
									value={evidenceUrl}
									onChange={(event) => setEvidenceUrl(event.target.value)}
									placeholder="https://"
								/>
								{evidenceOk ? null : (
									<p className="text-xs text-red-700">
										A link starts with https:// or http://.
									</p>
								)}
								{fieldError("evidenceUrl")}
							</div>
						</div>
						<div className="grid gap-3 sm:grid-cols-2">
							<div className="grid gap-1.5">
								<Label htmlFor="gst-signed-by">Signed off by</Label>
								<Input
									id="gst-signed-by"
									value={signedOffBy}
									onChange={(event) => setSignedOffBy(event.target.value)}
									placeholder="Name of the person who approved it"
								/>
								{fieldError("signedOffBy")}
							</div>
							<div className="grid gap-1.5">
								<Label htmlFor="gst-signed-on">Signed off on</Label>
								<Input
									id="gst-signed-on"
									type="date"
									value={signedOffOn}
									onChange={(event) => setSignedOffOn(event.target.value)}
								/>
								{fieldError("signedOffOn")}
							</div>
						</div>
						{correcting ? (
							<div className="grid gap-1.5">
								<Label htmlFor="gst-reason">What was wrong</Label>
								<Textarea
									id="gst-reason"
									rows={2}
									value={reason}
									onChange={(event) => setReason(event.target.value)}
								/>
								{fieldError("reason")}
							</div>
						) : null}
						<div className="grid gap-1.5">
							<Label htmlFor="gst-note">Note (optional)</Label>
							<Textarea
								id="gst-note"
								rows={2}
								value={note}
								onChange={(event) => setNote(event.target.value)}
							/>
						</div>
						{preview.isError && Object.keys(fieldErrors).length === 0 ? (
							<p role="alert" className="text-sm text-red-700">
								{apiErrorMessage(
									preview.error,
									"Could not work out the effect of this change.",
								)}
							</p>
						) : null}
					</fieldset>
				) : null}

				{step.name === "review" ? (
					<div className="grid gap-3">
						{notice ? (
							<p role="alert" className="text-sm text-amber-800">
								{notice}
							</p>
						) : null}
						{preview.isPending ? (
							<p className="text-sm text-muted-foreground">
								Working out the new effect…
							</p>
						) : (
							<GstRatePreviewPanel
								preview={step.preview}
								newRateBp={Math.round((ratePercent ?? 0) * 100)}
							/>
						)}
						{save.isError && !isStalePreview(save.error) ? (
							<p role="alert" className="text-sm text-red-700">
								{apiErrorMessage(save.error, "Could not save this rate.")}
							</p>
						) : null}
					</div>
				) : null}

				{step.name === "done" && result ? (
					<GstRepriceSummary
						headline={step.headline}
						result={result}
						repricing={reprice.isPending}
						error={
							reprice.isError
								? apiErrorMessage(reprice.error, "Could not re-price.")
								: null
						}
						onRepriceRemaining={() => reprice.mutate(step.prefix)}
					/>
				) : null}

				{step.name === "unknown" ? (
					<p role="alert" className="text-sm text-amber-800">
						The rate may have been saved. Reload the list before trying again.
					</p>
				) : null}
			</div>

			<div className="flex justify-end gap-2 border-t pt-3">
				{step.name === "form" ? (
					<>
						<Button variant="ghost" onClick={onClose} disabled={busy}>
							Cancel
						</Button>
						<Button
							onClick={() => {
								setNotice(null);
								preview.mutate();
							}}
							disabled={!formComplete || busy}
						>
							{preview.isPending ? "Working it out…" : "Review effect"}
						</Button>
					</>
				) : null}
				{step.name === "review" ? (
					<>
						<Button
							variant="ghost"
							onClick={() => {
								setNotice(null);
								setStep({ name: "form" });
							}}
							disabled={busy}
						>
							Back
						</Button>
						<Button
							onClick={() => save.mutate(step.preview.latestRateId)}
							disabled={busy}
						>
							{save.isPending ? "Saving and re-pricing…" : "Save and re-price"}
						</Button>
					</>
				) : null}
				{step.name === "done" || step.name === "unknown" ? (
					<Button onClick={onClose} disabled={busy}>
						Done
					</Button>
				) : null}
			</div>
		</Dialog>
	);
}
